import React, { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import tw, { styled } from 'twin.macro';
import { LockClosedIcon } from '@heroicons/react/solid';
import { up } from 'styled-breakpoints';
import classNames from 'classnames';

import CommonAnimatedLoader from '../common/animated/loader';
import { FormCheckout } from '../../entities/forms/checkout';
import { parameters, strings } from '../../localization/strings';
import { Bank } from '../../entities/bank';
import IMAGES from '../../assets/images';
import CommonButtonDefault from '../common/buttons/default';
import Heading3 from '../common/headings/heading3';
import StyledLabel from '../common/form/styled-label';
import StyledError from '../common/form/styled-error';
import AlertPrimary from '../common/alerts/alert-primary';
import { currency } from '../../services/formatter/currency';
import CheckoutDiscountFormComponent from './discount-form';
import { Coupon } from '../../entities/coupon';
import CheckoutTotalsComponent from './totals';
import { ShortAnswerProduct } from '../../entities/short-answer';
import { useSite } from '../../hooks/use-site';
import { useAuth } from '../../hooks/use-auth';
import { getPaymentMethods } from '../../services/api/payments';
import type { PaymentMethod } from '../../services/api/payments';
import { pushGTMEvent, GTM_EVENTS } from '../../utils/gtm-events';
import { getProductSlug } from '../../utils/product-names';
import { hasDiscount, getDiscountBadge } from '../../utils/pricing';
import { getPriceSplit } from '../../utils/price-split';

const BankButton = styled.button<{ active: boolean }>`
  height: 90px;
  ${tw`flex items-center justify-center p-2 p-5 bg-white text-gray-100 rounded-lg border border-content-400 hover:border-blue-400`}

  ${(props) => props.active && tw`border-blue-500`}

  ${up('sm')} {
    height: 130px;
  }
`;

const BankImage = styled.img`
  max-height: 30px;
  max-width: 90%;
  margin: 0 auto;
  display: inline-block;

  ${up('md')} {
    max-height: 80px;
    max-width: 100%;
  }
`;

const PAYMENT_METHOD_IMAGE_MAP: Record<string, string> = {
  SEB: IMAGES.BANKS.SEB,
  SWEDBANK: IMAGES.BANKS.SWEDBANK,
  CITADELE: IMAGES.BANKS.CITADELE,
  LUMINOR: IMAGES.BANKS.LUMINOR,
  MEDICINOS: IMAGES.BANKS.MEDICINOS,
  SIAULIU: IMAGES.BANKS.SIAULIU,
  CARD: IMAGES.BANKS.CARD,
  PL_PRZELEWY24: IMAGES.BANKS.PL_PRZELEWY24,
  BLIK: IMAGES.BANKS.BLIK,
};

const DEFAULT_PAYMENT_METHOD_IMAGE = IMAGES.BANKS.CARD;

const resolvePaymentMethodImage = (method: PaymentMethod): string => {
  if (method.image) {
    return method.image;
  }

  if (method.imageKey) {
    const lookupKey = method.imageKey.toUpperCase();

    if (PAYMENT_METHOD_IMAGE_MAP[lookupKey]) {
      return PAYMENT_METHOD_IMAGE_MAP[lookupKey];
    }
  }

  return DEFAULT_PAYMENT_METHOD_IMAGE;
};

interface ComponentProps {
  isFailed?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  personCode?: string;
  childCode?: string;
  preselectBank?: string;
  isChild?: boolean;
  age?: number;
  hasPurchased?: boolean;
  product: ShortAnswerProduct;
  extraProduct?: ShortAnswerProduct;
  onComplete: (data: FormCheckout) => void;
}

function CheckoutFormComponent({
  isFailed,
  firstName,
  lastName,
  email,
  phone,
  preselectBank,
  product,
  extraProduct,
  onComplete,
  age,
}: ComponentProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [discount, setDiscount] = useState<Coupon>();
  const price = extraProduct?.price ? Number(extraProduct.price) + Number(product.price) : product.price;
  const [total, setTotal] = useState<number>(price);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState<boolean>(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState<string>();
  const bankHeaderRef = useRef<HTMLDivElement>(null);
  const { locale, currency: siteCurrency } = useSite();
  const priceSplit = getPriceSplit(product.id);
  const displayPrice = priceSplit ? product.price - priceSplit.amount : product.price;
  const { user } = useAuth();

  const { register, handleSubmit, setValue, setError, clearErrors, watch, formState } = useForm<FormCheckout>();

  const isResearch =
    product.id === parameters().product.researchId || product.id === parameters().product.freeScreenerAsitId;

  useEffect(() => {
    let isMounted = true;

    const loadPaymentMethods = async () => {
      setIsLoadingBanks(true);
      setPaymentMethodsError(undefined);

      try {
        const paymentMethods = await getPaymentMethods(locale, user?.countryId);

        if (!isMounted) {
          return;
        }

        const normalizedBanks = paymentMethods.map<Bank>((method) => ({
          identifier: method.identifier,
          title: method.title,
          image: resolvePaymentMethodImage(method),
        }));

        setBanks(normalizedBanks);

        if (preselectBank && normalizedBanks.some((bank) => bank.identifier === preselectBank)) {
          setValue('bank', preselectBank, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
        } else if (normalizedBanks.length === 1) {
          setValue('bank', normalizedBanks[0].identifier, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true,
          });
        }
      } catch (error) {
        if (isMounted) {
          setPaymentMethodsError(strings().error.common.description);
          setBanks([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingBanks(false);
        }
      }
    };

    loadPaymentMethods();

    return () => {
      isMounted = false;
    };
  }, [locale, preselectBank, setValue, user?.countryId]);

  const selectedBank = watch('bank', preselectBank);

  const onSubmit: SubmitHandler<FormCheckout> = async (data) => {
    try {
      if (!data.bank && total > 0) {
        setError('bank', { message: strings().error.bank.required });
      } else {
        setIsLoading(true);
        const formData = data;

        if (firstName) {
          formData.firstName = firstName;
        }

        if (lastName) {
          formData.lastName = lastName;
        }

        if (email) {
          formData.email = email;
        }

        if (phone) {
          formData.phone = phone;
        }

        formData.isResearch = isResearch;
        await onComplete(formData);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      setError('common', { message: (e as Error).message });
    }
  };

  const onSelectBank = (selected: Bank) => {
    clearErrors('bank');
    setValue('bank', selected.identifier, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  const renderPaymentMethods = () => {
    if (isLoadingBanks) {
      return (
        <div className="flex items-center justify-center mt-6 min-h-[120px]">
          <CommonAnimatedLoader />
        </div>
      );
    }

    if (banks.length) {
      return (
        <div className="grid grid-cols-2 gap-6 mt-6">
          {banks.map((bank) => (
            <BankButton
              className="group"
              active={bank.identifier === selectedBank}
              type="button"
              onClick={() => onSelectBank(bank)}
              key={bank.identifier}
            >
              <BankImage
                className={`group-hover:grayscale-0 group-hover:opacity-100 ${
                  bank.identifier === selectedBank ? `grayscale-0 opacity-100` : `grayscale opacity-40`
                }`}
                src={bank.image}
                alt={bank.title}
              />
            </BankButton>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-6 text-sm text-content-600">{paymentMethodsError || strings().error.common.description}</div>
    );
  };

  const onChangeDiscountCode = (code?: Coupon) => {
    setValue('discountCode', code?.code || '');
    setDiscount(code);
    if (code) {
      setTotal(
        code.amountExact
          ? Math.max(0, price - code.amountExact)
          : Math.round(price * (100 - code.amountPercentage)) / 100,
      );
    } else {
      setTotal(product.price);
    }
  };

  register('bank', {
    validate: {
      required: (value) => {
        return !!value || Math.round(total) === 0;
      },
    },
  });

  useEffect(() => {
    if (product) {
      pushGTMEvent(GTM_EVENTS.INITIATE_CHECKOUT, {
        product_id: product.id,
        product_slug: getProductSlug(product.id),
        product_name: product.title,
        value: product.price,
        currency: siteCurrency,
      });
    }
  }, [product, locale, siteCurrency]);

  useEffect(() => {
    if (Object.keys(formState.errors).length === 1 && formState.errors.bank && bankHeaderRef.current) {
      window.scrollTo({ behavior: 'smooth', top: bankHeaderRef.current.offsetTop - 10 });
    }
  }, [formState]);

  const button = !isResearch ? strings().button.completedOrder : strings().button.signOrder;
  const { isSelfCo } = useSite();

  return (
    <main className="lg:min-h-full lg:overflow-hidden lg:flex lg:flex-row-reverse">
      {isLoading && <CommonAnimatedLoader />}
      <h1 className="sr-only">{strings().checkout.titleBilling}</h1>

      {/* Order summary */}
      {!isResearch && (
        <section aria-labelledby="summary-heading" className="bg-gray-50 w-full lg:max-w-md flex-col lg:flex pt-10">
          <h2 id="order-heading" className="sr-only">
            {strings().checkout.titleYourOrder}
          </h2>

          <ul className="divide-gray-200 px-6 pt-5 lg:pt-0">
            <li className="flex items-center py-6 space-x-6">
              {isSelfCo ? (
                <img
                  src={product.key === 'FOOD_ID' ? IMAGES.PRODUCTS.FOOD_INTOLERANCE_BOX : IMAGES.PRODUCTS.ALLERGY_BOX}
                  alt={strings().products[product.key]?.title || product.title}
                  className="flex-none w-20 h-20 sm:w-40 sm:h-40 object-center object-cover bg-gray-200 rounded-md"
                />
              ) : (
                <img
                  src={IMAGES.PRODUCTS.ALLERGO_EXPERT}
                  alt={strings().products[product.key]?.title || product.title}
                  className="flex-none w-20 h-20 sm:w-40 sm:h-40 object-center object-cover bg-gray-200 rounded-md"
                />
              )}

              <div className="flex flex-col items-center space-y-4">
                <div className="text-sm font-medium space-y-1">
                  <h3 className="text-gray-900 font-bold">{product.title}</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-gray-900">{currency(displayPrice, locale, siteCurrency)}</p>
                    {isSelfCo && hasDiscount(product) && (
                      <p className="text-red-500 line-through text-xs">
                        {currency(product.priceOriginal, locale, siteCurrency)}
                      </p>
                    )}
                  </div>
                  {isSelfCo && hasDiscount(product) && (
                    <span className="bg-[#FF6C6C] text-white text-xs px-2 py-0.5 rounded-full font-medium inline-block">
                      {getDiscountBadge(product)}
                    </span>
                  )}
                  {priceSplit && (
                    <div className="pt-1">
                      <h3 className="text-gray-900">{priceSplit.itemName}</h3>
                      <p className="text-gray-900">+{currency(priceSplit.amount, locale, siteCurrency)}</p>
                    </div>
                  )}
                </div>
                {!!extraProduct && (
                  <div className="text-sm font-medium space-y-1">
                    <h3 className="text-gray-900 font-bold">{extraProduct.title}</h3>
                    <p className="text-gray-900">{currency(extraProduct.price, locale, siteCurrency)}</p>
                  </div>
                )}
              </div>
            </li>
          </ul>

          <div className="sticky bottom-0 flex-none bg-gray-50 border-t border-gray-200 p-6">
            <CheckoutDiscountFormComponent onChange={onChangeDiscountCode} productId={product.id} />
            <CheckoutTotalsComponent
              subtotal={price}
              discount={discount}
              total={total}
              product={product}
              isSelfCo={isSelfCo}
            />
          </div>
        </section>
      )}
      {/* Checkout form */}
      <section
        aria-labelledby="payment-heading"
        className="flex-auto overflow-y-auto px-4 pt-7 md:pt-12 lg:mt-36 pb-16 sm:px-6 sm:pt-16 lg:px-8 lg:pt-0 lg:pb-24"
      >
        <div className="max-w-lg mx-auto">
          {isFailed && (
            <div className="mb-8">
              <AlertPrimary
                type="failed"
                title={strings().checkout.failed.title}
                text={strings().checkout.failed.description}
              />
            </div>
          )}

          {isResearch && age && age > 12 && (
            <Heading3 className="text-center">{strings().checkout.titleResearch}</Heading3>
          )}
          <form className="mt-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-12 gap-y-6 gap-x-4">
              {isResearch && age && age < 12 && (
                <>
                  <div className="col-span-full">
                    <Heading3>{strings().checkout.titleSpouseContactInfo}</Heading3>
                  </div>
                  <div className="mt-2 text-sm text-content-600 col-span-full">
                    {strings().checkout.explanationTextInfoForChildsResearch}
                  </div>
                  <div className="col-span-full">
                    <StyledLabel htmlFor="spouseName">{strings().form.spouseName}</StyledLabel>
                    <div className="mt-1">
                      <input
                        {...register('spouseFirstName', { required: true })}
                        type="text"
                        id="spouseFirstName"
                        name="spouseFirstName"
                        className={classNames(
                          'block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm',
                          {
                            'border-red-500': formState.errors.spouseFirstName,
                          },
                        )}
                      />
                      {formState.errors.spouseFirstName && (
                        <StyledError>{strings().error.firstName.required}</StyledError>
                      )}
                    </div>
                  </div>
                  <div className="col-span-full">
                    <StyledLabel htmlFor="spouseSurname">{strings().form.spouseSurname}</StyledLabel>
                    <div className="mt-1">
                      <input
                        {...register('spouseLastName', { required: true })}
                        type="text"
                        id="spouseLastName"
                        name="spouseLastName"
                        className={classNames(
                          'block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm',
                          {
                            'border-red-500': formState.errors.spouseLastName,
                          },
                        )}
                      />
                      {formState.errors.spouseLastName && (
                        <StyledError>{strings().error.lastName.required}</StyledError>
                      )}
                    </div>
                  </div>
                  <div className="col-span-full">
                    <StyledLabel htmlFor="spouseEmail">{strings().form.spouseEmail}</StyledLabel>
                    <div className="mt-1">
                      <input
                        {...register('spouseEmail', { required: true })}
                        type="email"
                        id="spouseEmail"
                        name="spouseEmail"
                        className={classNames(
                          'block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm',
                          {
                            'border-red-500': formState.errors.email,
                          },
                        )}
                      />
                      {formState.errors.spouseEmail && <StyledError>{strings().error.email.required}</StyledError>}
                    </div>
                  </div>
                </>
              )}
            </div>

            {total > 0 && (
              <>
                <Heading3 ref={bankHeaderRef} className="mt-10 md:mt-16">
                  {strings().checkout.titleChooseBank}
                </Heading3>
                {formState.errors.bank && <StyledError>{strings().error.bank.required}</StyledError>}
                {renderPaymentMethods()}
              </>
            )}

            {formState.errors.common && <StyledError>{formState.errors.common.message}</StyledError>}

            <CommonButtonDefault
              onClick={() => clearErrors('common')}
              type="submit"
              primary
              className="block w-full mt-6"
            >
              {total > 0 ? (
                <span className="flex flex-col md:flex-row items-center md:gap-2">
                  <span>
                    {strings().button.pay} {currency(total, locale, siteCurrency)}
                  </span>
                  {isSelfCo && hasDiscount(product) && (
                    <span className="line-through opacity-70 text-xs">
                      {currency(product.priceOriginal, locale, siteCurrency)}
                    </span>
                  )}
                </span>
              ) : (
                button
              )}
            </CommonButtonDefault>

            <div className="flex justify-center text-sm font-medium text-gray-500 mt-6">
              <LockClosedIcon className="w-5 h-5 text-gray-400 mr-1.5 flex-shrink-0" aria-hidden="true" />
              {isResearch ? (
                <div>{strings().checkout.SigningOfDocumentsDokobit}</div>
              ) : (
                <div>
                  {strings().checkout.checkboxPayseraText}{' '}
                  <a
                    className="underline"
                    href={parameters().paymentTermsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {strings().checkout.checkboxPayseraTerms}
                  </a>
                  {strings().checkout.checkboxPayseraAgreement}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

CheckoutFormComponent.defaultProps = {
  isFailed: false,
  isChild: false,
  hasPurchased: false,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preselectBank: '',
  personCode: '',
  childCode: '',
  age: 0,
  extraProduct: 0,
};

export default CheckoutFormComponent;
